import axios from 'axios';
import { API_BASE_URL } from '../config/environment';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL, // Use environment-based URL
  timeout: 360000, // 6 minutes - increased for itinerary generation (backend timeout is 5 minutes)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth headers if needed
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Allow callers to opt out of the global redirect (e.g. toggleSave shows a toast instead)
      if (!error.config?._skipAuthRedirect) {
        localStorage.removeItem('auth_token');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Places API
export const placesApi = {
  // Get all places
  getAll: () => api.get('/places'),

  // Get all places with pagination
  getAllPaginated: (page = 0, size = 20, sortBy = 'name', sortDir = 'ASC') => 
    api.get(`/places?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`),

  // Get place by ID
  getById: (id) => api.get(`/places/${id}`),

  // Search places (returns all results)
  search: (query) => api.get(`/places/search?q=${encodeURIComponent(query)}`),

  // Search places with pagination
  searchPaginated: (query, page = 0, size = 20, sortBy = 'name', sortDir = 'ASC') => 
    api.get(`/places/search?q=${encodeURIComponent(query)}&page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`),

  // Get places by country (returns all results)
  getByCountry: (country) => api.get(`/places/country/${encodeURIComponent(country)}`),

  // Get places by country with pagination
  getByCountryPaginated: (country, page = 0, size = 20, sortBy = 'name', sortDir = 'ASC') => 
    api.get(`/places/country/${encodeURIComponent(country)}?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`),

  // Get places by category (returns all results)
  getByCategory: (category) => api.get(`/places/category/${encodeURIComponent(category)}`),

  // Get places by category with pagination
  getByCategoryPaginated: (category, page = 0, size = 20, sortBy = 'name', sortDir = 'ASC') => 
    api.get(`/places/category/${encodeURIComponent(category)}?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`),

  // Get places by difficulty
  getByDifficulty: (level) => api.get(`/places/difficulty/${level}`),

  // Get places by age group
  getByAgeGroup: (ageGroup) => api.get(`/places/age-group/${encodeURIComponent(ageGroup)}`),

  // Get random places
  getRandom: (limit = 5) => api.get(`/places/random?limit=${limit}`),

  // Get places with games
  getWithGames: () => api.get('/places/with-games'),

  // Get all categories
  getCategories: () => api.get('/places/categories'),

  // Get all countries
  getCountries: () => api.get('/places/countries'),
};

// Games API
export const gamesApi = {
  // Get all active games
  getAll: () => api.get('/games'),

  // Get game by ID
  getById: (id) => api.get(`/games/${id}`),

  // Get games by place ID
  getByPlaceId: (placeId) => api.get(`/games/place/${placeId}`),

  // Get games by type
  getByType: (gameType) => api.get(`/games/type/${encodeURIComponent(gameType)}`),

  // Get games by difficulty
  getByDifficulty: (level) => api.get(`/games/difficulty/${level}`),

  // Get random games by difficulty
  getRandomByDifficulty: (level, limit = 3) => api.get(`/games/random/difficulty/${level}?limit=${limit}`),

  // Get games by place and type
  getByPlaceAndType: (placeId, gameType) => api.get(`/games/place/${placeId}/type/${encodeURIComponent(gameType)}`),

  // Count games by place
  countByPlace: (placeId) => api.get(`/games/place/${placeId}/count`),

  // Get all game types
  getTypes: () => api.get('/games/types'),
};

// Game Sessions API (for future implementation)
export const gameSessionsApi = {
  // Create new game session
  create: (sessionData) => api.post('/game-sessions', sessionData),

  // Update game session
  update: (sessionId, sessionData) => api.put(`/game-sessions/${sessionId}`, sessionData),

  // Get session by ID
  getById: (sessionId) => api.get(`/game-sessions/${sessionId}`),

  // Get user's sessions
  getByUser: (userId) => api.get(`/game-sessions/user/${userId}`),

  // Complete session
  complete: (sessionId, finalScore) => api.post(`/game-sessions/${sessionId}/complete`, { score: finalScore }),
};

// Utility functions for common API patterns
export const apiUtils = {
  // Handle API errors
  handleError: (error) => {
    if (error.response) {
      // Server responded with error status
      return {
        message: error.response.data?.message || 'Server Error',
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      // Request was made but no response received
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return {
          message: 'Request timed out. The itinerary generation is taking longer than expected. Please try again.',
          status: 0
        };
      }
      return {
        message: 'Network Error - Please check your connection',
        status: 0
      };
    } else {
      // Something else happened
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return {
          message: 'Request timed out. The itinerary generation is taking longer than expected. Please try again.',
          status: -1
        };
      }
      return {
        message: error.message || 'An unexpected error occurred',
        status: -1
      };
    }
  },

  // Create query string from object
  createQueryString: (params) => {
    const filtered = Object.entries(params)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    return filtered ? `?${filtered}` : '';
  },
};

// Recommendations API
export const recommendationsApi = {
  // Get AI-powered travel recommendations
  getRecommendations: (requestData) => 
    api.post('/v1/recommend', requestData),
  
  // Generate travel itinerary (with extended timeout for long-running LLM operations)
  generateItinerary: (requestData) => 
    api.post('/v1/recommend/itinerary', requestData, {
      timeout: 360000, // 6 minutes - matches backend async timeout of 5 minutes with buffer
    }),
  
  // Check itinerary creation limit for user
  checkItineraryLimit: (userId) =>
    api.get(`/v1/recommend/itinerary/limit?userId=${encodeURIComponent(userId)}`),

  // Suggest destinations from a freeform user query
  suggestDestinations: (requestData) =>
    api.post('/v1/recommend/destinations', requestData, {
      timeout: 30000, // 30 seconds
    }),

  // Health check for recommendations service
  health: () => api.get('/v1/recommend/health')
};

// Famous Places API
export const famousPlacesApi = {
  // Get all 50 famous places with full details
  getFamousPlaces: () => api.get('/v1/famous-places/places'),
  
  // Get list of famous places with status
  getList: () => api.get('/v1/famous-places/list'),
  
  // Get batch status
  getStatus: () => api.get('/v1/famous-places/status')
};

// Images API
export const imagesApi = {
  // Get all images for a place
  getPlaceImages: (placeId) => api.get(`/v1/images/place/${placeId}`),
  
  // Search images
  searchImages: (query, limit = 10, source = 'all') => 
    api.get(`/v1/images/search?q=${encodeURIComponent(query)}&limit=${limit}&source=${source}`),
  
  // Get license for an image
  getLicense: (imageUrl) => 
    api.get(`/v1/images/license?url=${encodeURIComponent(imageUrl)}`)
};

// Itineraries API
export const itinerariesApi = {
  // Generate AI itineraries for a country
  generateItineraries: (country, count = 7, userId = null) => {
    const params = new URLSearchParams({
      country,
      count: count.toString()
    });
    if (userId) {
      params.append('userId', userId);
    }
    return api.post(`/v1/itineraries/generate?${params.toString()}`);
  },

  // Get itineraries by country
  getByCountry: (country, userId = null) => {
    const url = userId
      ? `/v1/itineraries/country/${encodeURIComponent(country)}?userId=${encodeURIComponent(userId)}`
      : `/v1/itineraries/country/${encodeURIComponent(country)}`;
    return api.get(url);
  },

  // Get itinerary by ID
  getById: (id, userId = null) => {
    const url = userId
      ? `/v1/itineraries/${id}?userId=${encodeURIComponent(userId)}`
      : `/v1/itineraries/${id}`;
    return api.get(url);
  },
  
  // Get all countries with itinerary counts
  getCountriesWithCounts: () => api.get('/v1/itineraries/countries'),
  
  // Toggle favorite status for an itinerary
  toggleFavorite: (id, userId) => 
    api.post(`/v1/itineraries/${id}/favorite?userId=${encodeURIComponent(userId)}`),
  
  // Get featured itineraries with userId for favorite status
  getFeatured: (userId = null) => {
    const url = userId 
      ? `/v1/itineraries/featured?userId=${encodeURIComponent(userId)}`
      : '/v1/itineraries/featured';
    return api.get(url);
  },
  
  // Get favorited/saved itineraries for a user
  getFavorites: (userId) =>
    api.get(`/v1/itineraries/favorites?userId=${encodeURIComponent(userId)}`),

  // Get itineraries created by a user
  getCreatedByUser: (userId, limit = 10) =>
    api.get(`/v1/itineraries/created?userId=${encodeURIComponent(userId)}&limit=${limit}`),

  // Get user statistics
  getUserStats: (userId) =>
    api.get(`/v1/itineraries/stats?userId=${encodeURIComponent(userId)}`),

  // Check generation limit for a user
  checkGenerationLimit: (userId) =>
    api.get(`/v1/itineraries/check-limit?userId=${encodeURIComponent(userId)}`),

  // Get all itineraries (with limit)
  getAll: (limit = 50, userId = null) => {
    const url = userId
      ? `/v1/itineraries/all?limit=${limit}&userId=${encodeURIComponent(userId)}`
      : `/v1/itineraries/all?limit=${limit}`;
    return api.get(url);
  }
};

// Itinerary Generation API (Async with WebSocket progress)
export const itineraryGenerationApi = {
  // Start a new itinerary generation job
  startGeneration: (country, durationDays, itineraryType, budgetRange, userId = null, travelerProfile = null, travelPreferences = null, travelPersonality = null) => {
    const body = {
      country,
      durationDays,
      itineraryType,
      budgetRange,
      ...(userId && { userId }),
      ...(travelerProfile && { travelerProfile }),
      ...(travelPreferences && { travelPreferences }),
      ...(travelPersonality && { travelPersonality }),
    };
    return api.post('/itineraries/generate/start', body);
  },

  // Get job status
  getJobStatus: (jobId) =>
    api.get(`/itineraries/generate/status/${jobId}`),

  // Retry a failed job
  retryJob: (jobId) =>
    api.post(`/api/itineraries/generate/retry/${jobId}`),

  // Cancel a running job
  cancelJob: (jobId) =>
    api.post(`/api/itineraries/generate/cancel/${jobId}`)
};

// Place Metadata API
export const placeMetadataApi = {
  // Get metadata for a place by name
  getMetadata: (placeName) => api.get(`/v1/place-metadata/${encodeURIComponent(placeName)}`),
  
  // Get enhanced metadata for a place by name
  getEnhancedMetadata: (placeName) => api.get(`/v1/place-metadata/enhanced/${encodeURIComponent(placeName)}`),
  
  // Generate metadata for a place
  generateMetadata: (place, country) => 
    api.post(`/v1/place-metadata/generate?place=${encodeURIComponent(place)}&country=${encodeURIComponent(country || '')}`),
  
  // Generate enhanced metadata for a place (with all wireframe fields)
  generateEnhancedMetadata: (place, country) => 
    api.post(`/v1/place-metadata/generate-enhanced?place=${encodeURIComponent(place)}&country=${encodeURIComponent(country || '')}`),
};

// UNESCO Heritage Sites API
export const unescoApi = {
  // Get all UNESCO sites with pagination
  getAll: (page = 0, size = 20, sortBy = 'name', sortDir = 'ASC') => 
    api.get(`/unesco/all?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`),
  
  // Get sites by country
  getByCountry: (country, page = 0, size = 20) => 
    api.get(`/unesco/${encodeURIComponent(country)}?page=${page}&size=${size}`),
  
  // Get site by ID
  getById: (id) => api.get(`/unesco/id/${id}`),
  
  // Search UNESCO sites
  search: (query, page = 0, size = 20) => 
    api.get(`/unesco/search?q=${encodeURIComponent(query)}&page=${page}&size=${size}`),
};

// System/Health API
// Seasonal Events API
export const seasonalEventsApi = {
  // Get seasonal events
  getAll: (eventType = null, limit = 10) => {
    const params = new URLSearchParams();
    if (eventType) params.append('eventType', eventType);
    params.append('limit', limit.toString());
    return api.get(`/v1/seasonal-events?${params.toString()}`);
  },
  
  // Get single seasonal event by ID
  getById: (eventType, eventId) => 
    api.get(`/v1/seasonal-events/${encodeURIComponent(eventType)}/${encodeURIComponent(eventId)}`),
};

export const systemApi = {
  health: () => api.get('/health'),
  info: () => api.get('/info'),
};

// Hotels API
export const hotelsApi = {
  /**
   * Search hotels by destination and dates
   * @param {Object} request - Hotel search parameters
   * @param {string} request.destination - City or country name
   * @param {string} request.checkIn - Check-in date (YYYY-MM-DD)
   * @param {string} request.checkOut - Check-out date (YYYY-MM-DD)
   * @param {number} request.guests - Number of guests (default: 2)
   * @param {string} request.budget - Budget tier: "low", "medium", "high"
   * @param {number} request.limit - Max results (default: 6)
   * @returns {Promise} List of hotel results
   */
  searchHotels: async (request, retryCount = 0) => {
    try {
      // Add a small delay to respect CAPTCHA rate limiting (2 seconds minimum)
      // Only delay on retry attempts, not the first request
      if (retryCount > 0) {
        const delayMs = Math.min(2000 * retryCount, 10000); // Max 10 seconds
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
      
      const response = await api.post('/v1/hotels/search', {
        destination: request.destination,
        checkIn: request.checkIn,
        checkOut: request.checkOut,
        guests: request.guests || 2,
        budget: request.budget || 'medium',
        rooms: request.rooms || 1,
        currency: request.currency || 'USD',
        limit: request.limit || 6,
        sortBy: request.sortBy || 'popularity'
      });
      return response.data;
    } catch (error) {
      // Check if it's a CAPTCHA rate limit error (403 Forbidden)
      const isRateLimitError = error?.response?.status === 403 || 
                               error?.response?.status === 429 ||
                               (error?.response?.status === 400 && 
                                error?.response?.data?.message?.toLowerCase().includes('captcha'));
      
      // Retry up to 2 times for rate limit errors with exponential backoff
      if (isRateLimitError && retryCount < 2) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Hotel search rate limited, retrying in ${2 * (retryCount + 1)} seconds...`);
        }
        return hotelsApi.searchHotels(request, retryCount + 1);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Error searching hotels:', error);
      }
      // Return empty array on error to handle gracefully in UI
      return [];
    }
  },

  /**
   * Health check for hotel service
   */
  health: () => api.get('/v1/hotels/health').catch(() => ({ status: 'down' })),
};

// Standardized Place Content API
export const standardizedPlaceContentApi = {
  // Get standardized content by place name
  getByPlaceName: (placeName) => 
    api.get(`/standardized-place-content/by-name/${encodeURIComponent(placeName)}`),

  // Get standardized content by source place ID
  getBySourcePlaceId: (placeId) => 
    api.get(`/standardized-place-content/by-source/${placeId}`),

  // Standardize a single place
  standardizePlace: (placeId, adminApiKey = null) => {
    const headers = adminApiKey ? { 'X-Admin-API-Key': adminApiKey } : {};
    return api.post(`/standardized-place-content/standardize/${placeId}`, {}, { headers });
  },

  // Standardize multiple places (batch)
  standardizeBatch: (limit = 5, adminApiKey = null) => {
    const headers = adminApiKey ? { 'X-Admin-API-Key': adminApiKey } : {};
    return api.post(`/standardized-place-content/standardize-batch?limit=${limit}`, {}, { headers });
  },
};

// Feedback API
export const feedbackApi = {
  // Rate an itinerary (thumbs up/down)
  rateItinerary: (itineraryId, userId, rating) =>
    api.post(`/v1/feedback/itinerary/${itineraryId}/rate?userId=${userId}`, { rating }),

  // Provide activity-level feedback
  rateActivity: (userId, request) =>
    api.post(`/v1/feedback/activity/rate?userId=${userId}`, request),

  // Get itinerary rating statistics
  getItineraryRating: (itineraryId, userId) =>
    api.get(`/v1/feedback/itinerary/${itineraryId}/rating?userId=${userId}`),
};

// Alternatives API
export const alternativesApi = {
  // Get alternatives for an activity
  getAlternatives: (itineraryId, day, activityIndex, userId) =>
    api.get(`/v1/alternatives/itinerary/${itineraryId}/day/${day}/activity/${activityIndex}?userId=${userId}`),

  // Swap activity with alternative
  swapActivity: (itineraryId, userId, request) =>
    api.post(`/v1/alternatives/itinerary/${itineraryId}/swap?userId=${userId}`, request),

  // Search for places (manual search)
  searchPlaces: (query, city, activityType, userId) => {
    const params = new URLSearchParams({ query, userId });
    if (city) params.append('city', city);
    if (activityType) params.append('activityType', activityType);
    return api.get(`/v1/alternatives/search?${params.toString()}`);
  },

  // Get place details
  getPlaceDetails: (placeId, activityType, userId) => {
    const params = new URLSearchParams({ userId });
    if (activityType) params.append('activityType', activityType);
    return api.get(`/v1/alternatives/place/${placeId}?${params.toString()}`);
  },
};

// Itinerary Edit API (edit-mode modifications on fullItineraryJson)
export const itineraryEditApi = {
  editItinerary: (itineraryId, userId, request) =>
    api.post(`/v1/itinerary/${itineraryId}/edit?userId=${encodeURIComponent(userId)}`, request),
};

// Guides API (v1 — LLM-generated country travel guides)
export const guidesApi = {
  getAll: () => api.get('/v1/guides'),
  getBySlug: (slug) => api.get(`/v1/guides/${encodeURIComponent(slug)}`),
};

// Explore API (v2 — paginated explore feed, map pins, save/unsave)
export const exploreApi = {
  getExplore: (params) => api.get('/v2/itineraries/explore', { params }),
  getMapPins: (params) => api.get('/v2/itineraries/explore/map-pins', { params }),
  toggleSave: (id) => api.post(`/v2/itineraries/${id}/save`, null, {
    // Skip the global 401 redirect — we handle it locally with a toast
    _skipAuthRedirect: true,
  }),
  getSaved: (page = 0, size = 20) => api.get('/v2/itineraries/saved', { params: { page, size } }),
};

export default api;